import React, { useState, useEffect } from 'react';
import { AlertTriangle, User } from 'lucide-react';
import { supabase } from '../../../../../supabaseClient';

interface CommentItem {
  id: string | number;
  user: string | null;
  text: string;
  date: string;
}

// Função para calcular o "tempo atrás" (estilo YouTube)
const formatTimeAgo = (dateString?: string) => {
  if (!dateString) return 'Agora mesmo';
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'há poucos segundos';
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `há ${diffInMinutes} minuto${diffInMinutes > 1 ? 's' : ''}`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `há ${diffInHours} hora${diffInHours > 1 ? 's' : ''}`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return `há ${diffInDays} dia${diffInDays > 1 ? 's' : ''}`;
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) return `há ${diffInMonths} mês${diffInMonths > 1 ? 'es' : ''}`;
  const diffInYears = Math.floor(diffInDays / 365);
  return `há ${diffInYears} ano${diffInYears > 1 ? 's' : ''}`;
};

export default function PlayFeedChat({ videoId }: { videoId: string }) {
  const [messages, setMessages] = useState<CommentItem[]>([]);
  const [inputVal, setInputVal] = useState('');
  const [optionalName, setOptionalName] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // 🛡️ Regras de segurança (Lista Negra + Links)
  const forbiddenWords = ['bet', 'aposta', 'tigrinho', 'cassino', 'casino', 'roleta', 'slots', 'dinheiro fácil', 'ganhar dinheiro'];

  const isMessageSafe = (text: string) => {
    const lowerText = text.toLowerCase();
    const hasLink = /(http|https|www\.|[a-zA-Z0-9-]+\.[a-zA-Z]{2,})/.test(lowerText);
    if (hasLink) return { safe: false, reason: 'Links não são permitidos no chat.' };
    
    const hasForbiddenWord = forbiddenWords.some(word => lowerText.includes(word));
    if (hasForbiddenWord) return { safe: false, reason: 'Mensagem viola as regras da comunidade (Jogos/Spam).' };

    return { safe: true, reason: '' };
  };

  // 📥 Buscar comentários estritamente deste vídeo no Supabase
  const fetchComments = async () => {
    if (!videoId) return;
    try {
      const { data, error } = await supabase
        .from('comments')
        .select('*')
        .eq('video_id', videoId)
        .order('created_at', { ascending: false })
        .limit(20); // 👈 Só precisas adicionar esta linha!

      if (error) {
        setErrorMsg('Não foi possível carregar os comentários.');
        return;
      }

      if (data) {
        const formatted = data.map((c: any) => ({
          id: c.id,
          user: c.user_name || 'Usuário Anônimo',
          text: c.text || '',
          date: c.created_at // 👈 Passa a data real do Supabase sem formatar para o formatTimeAgo usar
        }));
        setMessages(formatted);
      }
    } catch {
      setErrorMsg('Erro ao carregar os comentários.');
    }
  };

  useEffect(() => {
    setMessages([]);
    setInputVal('');
    setErrorMsg('');
    fetchComments();
  }, [videoId]);

  // 📤 Enviar comentário amarrado ao ID exato do vídeo
  const handleSend = async () => {
    const message = inputVal.trim();
    if (!message || !videoId || loading) return;

    const check = isMessageSafe(message);
    if (!check.safe) {
      setErrorMsg(check.reason);
      setTimeout(() => setErrorMsg(''), 4000);
      return;
    }

    setErrorMsg('');
    setLoading(true);
    const userName = optionalName.trim() ? optionalName.trim() : 'Usuário Anônimo';

    try {
      const { data, error } = await supabase
        .from('comments')
        .insert({ 
          video_id: videoId, 
          text: message, 
          user_name: userName 
        })
        .select()
        .single();

      if (error) {
        setErrorMsg(error.message || 'Não foi possível enviar o comentário.');
        return;
      }

      const newEntry: CommentItem = {
        id: data?.id || Date.now(),
        user: data?.user_name || userName,
        text: data?.text || message,
        date: data?.created_at || new Date().toISOString() // 👈 Data real no momento do envio
      };

      setMessages(prev => [newEntry, ...prev]);
      setInputVal('');
    } catch {
      setErrorMsg('Ocorreu um erro inesperado. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ width: '100%', marginTop: '20px', fontFamily: 'Roboto, Arial, sans-serif' }}>
      
      {/* Título: Total de Comentários */}
      <h3 style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold', margin: '0 0 20px 0' }}>
        {messages.length} comentários
      </h3>

      {errorMsg && (
        <div style={{ backgroundColor: 'rgba(229, 9, 20, 0.1)', border: '1px solid #E50914', color: '#E50914', padding: '6px 10px', borderRadius: '4px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
          <AlertTriangle size={14} />
          {errorMsg}
        </div>
      )}

      {/* Área de Escrever Comentário */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '32px' }}>
        {/* Avatar Falso */}
        <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <User size={20} color="#aaa" />
        </div>
        
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <input 
            type="text" 
            placeholder="Seu nome (Opcional)" 
            value={optionalName}
            onChange={(e) => setOptionalName(e.target.value)}
            style={{ width: '200px', backgroundColor: 'transparent', border: 'none', borderBottom: '1px solid #444', color: '#fff', padding: '4px 0', fontSize: '13px', outline: 'none' }} 
          />
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
            <input 
              type="text" 
              placeholder={loading ? 'Adicionando comentário...' : 'Adicione um comentário...'} 
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !loading) { e.preventDefault(); handleSend(); }}}
              disabled={loading}
              style={{ flex: 1, backgroundColor: 'transparent', border: 'none', borderBottom: '1px solid #aaa', padding: '4px 0', color: '#fff', outline: 'none', fontSize: '14px', opacity: loading ? 0.7 : 1 }} 
            />
            <button 
              type="button"
              onClick={handleSend}
              disabled={loading || !inputVal.trim()}
              style={{ backgroundColor: loading || !inputVal.trim() ? '#333' : '#3ea6ff', color: loading || !inputVal.trim() ? '#888' : '#000', border: 'none', borderRadius: '18px', padding: '8px 16px', fontSize: '13px', fontWeight: 'bold', cursor: loading || !inputVal.trim() ? 'default' : 'pointer', transition: '0.2s' }}
            >
              Responder
            </button>
          </div>
        </div>
      </div>

      {/* Lista de Comentários */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {messages.map((m) => (
          <div key={m.id} style={{ display: 'flex', gap: '16px' }}>
            {/* Avatar do Usuário */}
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#222', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <User size={20} color="#777" />
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {/* Transforma o nome num @handle, ex: @marcia */}
                <span style={{ color: '#fff', fontSize: '13px', fontWeight: 'bold' }}>
                  @{m.user ? m.user.replace(/\s+/g, '').toLowerCase() : 'usuario'}
                </span>
                <span style={{ color: '#aaa', fontSize: '12px' }}>{formatTimeAgo(m.date)}</span>
              </div>
              
              <p style={{ color: '#fff', fontSize: '14px', margin: '4px 0', lineHeight: '1.4' }}>
                {m.text}
              </p>
              
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}